// 1. External libraries (React first)
import { useMemo } from 'react'
import { gql } from '@apollo/client'

// 2. Type imports
import type { UseListProps, UseListResult } from '@ztechapps/common.hook'
import type { LowProductInventory } from '@ztechapps/common.contract'

// 3. Internal imports
import { StorageType, useList } from '@ztechapps/common.hook'
import { ProductStatusEnum } from '@ztechapps/common.contract'
import merge from 'lodash/merge'

export const productMinimumQuantityListQuery = gql`
  query productMinimumQuantityList($filter: JSON!, $options: JSON) {
    productMinimumQuantityList(filter: $filter, options: $options) {
      _id
      code
      name
      minimumQuantityAllowance
      computedQuantityOnHand
      requiredQuantity
      hasSubComponents
      remarks
      lastComputedAt
      baseUom {
        _id
        symbol
      }
      unitOfMeasurePrecision
    }
  }
`

export type UseProductMinimumQuantityListProps = Partial<UseListProps>

// Default props constant - extracted to avoid recreation on every render
const DEFAULT_PROPS: Omit<UseListProps, 'query'> = {
  entity: 'productMinimumQuantityList',
  limit: 0,
  filter: {
    status: ProductStatusEnum.Active,
    minimumQuantityAllowance: { $gt: 0 },
    forSale: true,
  },
  sort: {
    code: 1,
  },
  listActionOptions: {
    storageType: StorageType.sessionStorage,
  },
}

/**
 * Hook for fetching products with minimum quantity constraints.
 * Provides default filters for active products with minimumQuantityAllowance > 0 and forSale = true.
 *
 * @param props - Partial UseListProps to customize query behavior
 * @param props.query - Optional GraphQL query to override the default productMinimumQuantityListQuery
 * @param props.filter - Optional filter object to merge with default filters
 * @param props.sort - Optional sort object to override default sort ({ code: 1 })
 * @param props.limit - Optional limit to override default limit (0)
 * @param props.listActionOptions - Optional list action options (storage key, storage type, etc.)
 * @returns UseListResult<LowProductInventory> - List result with low inventory items
 *
 * @example
 * ```tsx
 * // Basic usage with default filters
 * const listResult = useProductMinimumQuantityList({
 *   listActionOptions: {
 *     storageKey: 'my-storage-key',
 *   },
 * })
 *
 * // Override filter
 * const listResult = useProductMinimumQuantityList({
 *   filter: {
 *     status: ProductStatusEnum.Active,
 *     minimumQuantityAllowance: { $gt: 5 },
 *   },
 * })
 * ```
 */
export const useProductMinimumQuantityList = ({
  query,
  ...rest
}: UseProductMinimumQuantityListProps): UseListResult<LowProductInventory> => {
  const mergeUseListProps = useMemo(() => {
    // We cannot merge directly the query because lodash will cause an infinite loop
    // so we remove it first and assign it later

    // Merge rest props with default props (excluding query)
    let finalProps = merge({}, DEFAULT_PROPS, rest) as UseListProps
    finalProps = {
      ...finalProps,
      query: query ?? productMinimumQuantityListQuery,
    }

    return finalProps
  }, [query, rest])

  return useList<LowProductInventory>(mergeUseListProps)
}
